import React, { useEffect, useRef, useState } from 'react';
import './index.css';

const services = [
  {
    number: '01',
    title: 'Delivery under pressure',
    body: 'When the roadmap is crowded and the consequences are real, we bring senior engineers into the room, find the constraint, and move the work forward.',
    tags: ['Tiger teams', 'Technical leadership', 'Rescue work'],
  },
  {
    number: '02',
    title: 'Systems that hold up',
    body: 'We design and build secure, observable platforms that stay useful when traffic, teams, and requirements change at the same time.',
    tags: ['Cloud architecture', 'Platform engineering', 'Security'],
  },
  {
    number: '03',
    title: 'Capability that compounds',
    body: 'We leave teams stronger than we found them through practical training, embedded coaching, and operating habits built for the real world.',
    tags: ['SRE', 'DevOps', 'Forward deployed engineering'],
  },
  {
    number: '04',
    title: 'Transformation that ships',
    body: 'We help organizations turn digital transformation from a broad ambition into secure software, practical operating changes, and measurable delivery progress.',
    tags: ['Digital transformation', 'Software delivery', 'Web platforms'],
  },
];

const operatingModes = [
  ['01', 'Assess the constraint'],
  ['02', 'Embed with the team'],
  ['03', 'Build and transfer'],
];

function Mark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function Arrow() {
  return <span aria-hidden="true" className="arrow">↗</span>;
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const menuButtonRef = useRef(null);
  const firstMenuLinkRef = useRef(null);

  const closeMenu = () => setMenuOpen(false);
  useEffect(() => {
    if (!menuOpen) return undefined;

    document.body.style.overflow = 'hidden';
    firstMenuLinkRef.current.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const subject = encodeURIComponent(`Lala Dynamics inquiry from ${data.get('name')}`);
    const body = encodeURIComponent(
      `Name: ${data.get('name')}\nEmail: ${data.get('email')}\n\nWhat we are working through:\n${data.get('message')}`
    );
    window.location.href = `mailto:contact@laladynamics.com?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="wordmark" href="#top" onClick={closeMenu}>
          <Mark />
          <span>LALA<br /><em>DYNAMICS</em></span>
        </a>
        <button ref={menuButtonRef} className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-controls="mobile-navigation" aria-label="Toggle navigation">
          <span /><span />
        </button>
        <nav className="nav desktop-nav" aria-label="Main navigation">
          <a href="#approach" onClick={closeMenu}>Approach</a>
          <a href="#capabilities" onClick={closeMenu}>Capabilities</a>
            <a href="#work" onClick={closeMenu}>How we engage</a>
          <a className="nav-contact" href="#contact" onClick={closeMenu}>Start a conversation <Arrow /></a>
        </nav>
        {menuOpen && (
          <nav id="mobile-navigation" className="nav mobile-nav" aria-label="Mobile navigation">
            <a ref={firstMenuLinkRef} href="#approach" onClick={closeMenu}>Approach</a>
            <a href="#capabilities" onClick={closeMenu}>Capabilities</a>
            <a href="#work" onClick={closeMenu}>How we engage</a>
            <a className="nav-contact" href="#contact" onClick={closeMenu}>Start a conversation <Arrow /></a>
          </nav>
        )}
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-grid" />
          <div className="hero-copy reveal">
            <p className="eyebrow"><span className="status-dot" /> Independent engineering partner</p>
            <h1>Hard problems.<br /><span>Handled.</span></h1>
            <p className="hero-lede">Lala Dynamics helps leaders turn difficult technical delivery into durable momentum — with the engineers, systems, and clarity to make the next move count.</p>
            <div className="hero-actions">
              <a className="button button-bright" href="#contact">Tell us what’s stuck <Arrow /></a>
              <a className="text-link" href="#approach">See how we work <span>↓</span></a>
            </div>
          </div>
          <div className="hero-aside reveal delay-one" aria-hidden="true">
            <div className="orbit orbit-one" />
            <div className="orbit orbit-two" />
            <div className="hero-coordinate">BUILD / SCALE<br />SECURE / TRANSFER</div>
            <div className="hero-note"><span>Field note / 001</span><p>“Speed is a systems property.”</p></div>
          </div>
          <div className="hero-foot"><span>Scroll to inspect</span><span className="line" /><span>01—05</span></div>
        </section>

        <section className="statement" id="approach">
          <div className="section-kicker">The useful kind of different</div>
          <div className="statement-content">
            <h2>Not a slide deck.<br /><span>A working partner.</span></h2>
            <div>
              <p>There is a moment in every ambitious program when strategy meets reality: a platform that cannot scale, a team carrying too much, a launch that cannot slip.</p>
              <p>That is where we work. Lala Dynamics is a small, senior team that joins the problem directly. We make the unknown legible, the work executable, and the outcome something your people can own.</p>
              <a className="text-link dark-link" href="#contact">Start a conversation <Arrow /></a>
            </div>
          </div>
        </section>

        <section className="capabilities" id="capabilities">
          <div className="section-heading">
            <div className="section-kicker">What we bring to the room</div>
            <h2>Three ways to<br /><span>change the trajectory.</span></h2>
          </div>
          <div className="service-list">
            {services.map((service) => (
              <article className="service-row" key={service.number}>
                <div className="service-number">{service.number}</div>
                <div className="service-main">
                  <h3>{service.title}</h3>
                  <p>{service.body}</p>
                  <div className="tag-list">{service.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                </div>
                <div className="service-arrow"><Arrow /></div>
              </article>
            ))}
          </div>
        </section>

        <section className="signal-band">
          <div className="signal-label">Lala / operating principle</div>
          <div className="signal-quote">Make the complex<br /><span>calm enough to act on.</span></div>
          <div className="signal-aside">Good engineering is not a performance.<br />It is the confidence to make a decision.</div>
        </section>

        <section className="work" id="work">
          <div className="work-intro">
            <div className="section-kicker">How we engage</div>
            <h2>Built for the<br /><span>problem at hand.</span></h2>
            <p>Every engagement starts with the constraint, not a prepackaged playbook. We assemble the right senior capability and work alongside your team.</p>
          </div>
          <div className="work-cards">
            <article className="work-card work-card-large">
              <div className="card-image image-cobalt"><div className="diagram"><b>01</b><span>OBSERVABILITY<br />IS A PRODUCT</span><i /></div></div>
              <div className="card-meta"><span>Embedded tiger team</span><span>Focused delivery <Arrow /></span></div>
              <h3>Resolve a specific, high-impact problem without creating a permanent dependency.</h3>
            </article>
            <article className="work-card">
              <div className="card-image image-coral"><div className="terminal"><span>engagement / active</span><b>✓</b><span>build · learn · transfer</span></div></div>
              <div className="card-meta"><span>Engineering enablement</span><span>Durable capability <Arrow /></span></div>
              <h3>Train and equip SRE, DevOps, and forward deployed engineers through real delivery work.</h3>
            </article>
          </div>
        </section>

        <section className="proof-strip">
          {operatingModes.map(([number, label]) => <div className="proof-item" key={label}><strong>{number}</strong><span>{label}</span></div>)}
          <div className="proof-mark">LD<span>+</span></div>
        </section>

        <section className="contact" id="contact">
          <div className="contact-copy">
            <div className="section-kicker">The next move</div>
            <h2>Bring us the<br /><span>hard part.</span></h2>
            <p>Tell us what is not moving. We will tell you what we see, what we would do first, and whether we are the right people to do it with you.</p>
            <div className="contact-details">
              <span>Focused engagements and long-term partnerships</span>
              <dl className="contact-lines">
                <div className="contact-line">
                  <dt>Email</dt>
                  <dd><a href="mailto:contact@laladynamics.com">contact@laladynamics.com</a></dd>
                </div>
                <div className="contact-line">
                  <dt>Phone</dt>
                  <dd><a href="tel:+14087685903">+1 408 768 5903</a></dd>
                </div>
                <div className="contact-line">
                  <dt>Office</dt>
                  <dd>
                    <address>
                      601 Farrington St<br />
                      Mountain House, CA 95391
                    </address>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <form className="contact-form" onSubmit={handleSubmit}>
            <label>Your name<input required name="name" placeholder="How should we call you?" /></label>
            <label>Work email<input required type="email" name="email" placeholder="you@company.com" /></label>
            <label>What are you working through?<textarea required name="message" rows="3" placeholder="A few useful specifics..." /></label>
            <button className="button button-dark" type="submit">Open email draft <Arrow /></button>
            {sent && <p className="email-handoff" role="status">We attempted to open your email app with a prepared draft. Please review and send it there; this website has not submitted your message.</p>}
          </form>
        </section>
      </main>
      <footer className="footer"><a className="wordmark footer-mark" href="#top"><Mark /><span>LALA<br /><em>DYNAMICS</em></span></a><span>Engineering for the moment it matters.</span><span>© 2026 Lala Dynamics LLC</span></footer>
    </div>
  );
}

export default App;